import { ApolloError, useLazyQuery } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'

import { CartFragment, GetGuestCartDocument } from '../../../graphql/magento'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { updateGuestCartId } from '../state/cart'
import { isCartNotFoundError, isCartOwnershipError } from '../utils'
import { useGuestCartId } from './useGuestCartId'

export type UseGuestCartGet = [
  getGuestCart: (shouldRefetch: boolean) => void,
  state: {
    cart?: CartFragment
    loading: boolean
    error?: ApolloError | Error
    called: boolean
    cartId: string | undefined
  },
  methods: {
    clearCartId: () => void
  },
]

export const useGuestCartGet = (isGuest: boolean): UseGuestCartGet => {
  const [called, setCalled] = useState(false)
  const [refetching, setRefetching] = useState(false)
  const [
    doGetGuestCartId,
    {
      cartId,
      loading: loadingCartId,
      error: errorCartId,
      called: calledCartId,
      recreated: recreatedCartId,
      clearCartId: clearGuestCartId,
      recreateCartId,
    },
  ] = useGuestCartId()

  const [
    doGetGuestCart,
    { data, loading: loadingCart, error: errorCart, called: calledCart, refetch: refetchGuestCart },
  ] = useLazyQuery(GetGuestCartDocument, {
    // return partial data if available
    errorPolicy: 'all',
  })

  // useRef to track the last cartId for which getGuestCart was called
  const cartCalledForCartIdRef = useRef<string | null>(null)

  const getGuestCart = useCallback(
    (shouldRefetch: boolean) => {
      if (!shouldRefetch) {
        setCalled(true)
        return
      }
      // see `useCustomerCartGet`: the lazy query's `loading` does not cover a refetch
      const settle = () => setRefetching(false)
      setRefetching(true)
      refetchGuestCart().then(settle, settle)
    },
    [refetchGuestCart],
  )

  const loading = loadingCartId || loadingCart || refetching

  // both are recovered from below by replacing the cart id, so neither is surfaced as a cart error
  const isCartUnusable = isCartNotFoundError(errorCart) || isCartOwnershipError(errorCart)
  const error = errorCartId || (isCartUnusable ? undefined : errorCart)

  useLogAndCaptureError(error)

  const cart = data?.cart || undefined

  const clearCartId = useCallback(() => {
    clearGuestCartId()
    setCalled(false)
    // Update the guest cart ID observable when clearing
    updateGuestCartId(null)
  }, [clearGuestCartId])

  useEffect(() => {
    if (!isGuest || !called || loadingCartId || calledCartId || errorCartId) {
      return
    }
    if (cartId) {
      return
    }
    doGetGuestCartId()
  }, [called, cartId, doGetGuestCartId, isGuest, loadingCartId, calledCartId, errorCartId])

  useEffect(() => {
    if (!called || loading || recreatedCartId) {
      return
    }
    if (!isCartUnusable) {
      return
    }
    if (isGuest) {
      recreateCartId()
    } else {
      clearCartId()
    }
  }, [called, clearCartId, isGuest, loading, isCartUnusable, recreateCartId, recreatedCartId])

  useEffect(() => {
    if (!called || loadingCart || calledCart || errorCart) {
      return
    }
    if (!cartId) {
      return
    }
    doGetGuestCart({
      variables: { cartId },
    })
    cartCalledForCartIdRef.current = cartId
  }, [called, cartId, doGetGuestCart, loadingCart, calledCart, errorCart])

  useEffect(() => {
    const isGuestCartCalledForId = cartCalledForCartIdRef.current === cartId
    if (loadingCartId || !cartId || !refetchGuestCart || !calledCart || isGuestCartCalledForId) {
      // if cartId is undefined or already called for this cartId, do not call again
      return
    }
    refetchGuestCart({ cartId })
    cartCalledForCartIdRef.current = cartId
  }, [calledCart, cartId, loadingCartId, refetchGuestCart])

  return [getGuestCart, { cart, loading, error, called, cartId }, { clearCartId }]
}
